import React, { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getFeedCardsAction, checkEditorialPermissionAction } from "../../../../../redux/actions/feedCardAction";
import { SectionLoader } from "../../../../../components/PageLoader";
import FeedCardDispatcher from "./cards/FeedCardDispatcher";
import LeaderboardWidget from "./LeaderboardWidget";

/**
 * WorkbrookTab — the platform-curated activity feed.
 *
 * Renders activity cards (NewJobPosted, ReferralMilestone, WeeklyStats,
 * TrendingRole, CandidateMatchAlert) fetched from GET /feed-cards.
 *
 * Features:
 *   - Cards in reverse chronological order (newest first)
 *   - Infinite scroll (paginated GET /feed-cards?page=N)
 *   - Empty state fallback (PRD §10): "Nothing new this week yet..."
 *   - "Write Article" button for users with editorial permission
 *
 * This tab has its own redux slice (feedCards) separate from the
 * Community tab's feedReducer, so the two tabs are independent streams.
 *
 * Props:
 *   onSwitchToCommunity — callback to switch to the Community tab
 */
const WorkbrookTab = ({ onSwitchToCommunity }) => {
  const dispatch = useDispatch();
  const isMounted = useRef(true);

  const { cards, loading, hasMore, currentPage } = useSelector(
    (store) => store.feedCards || { cards: [], loading: false, hasMore: false, currentPage: 1 }
  );

  // Sentinel ref for infinite scroll intersection observer
  const sentinelRef = useRef(null);

  // Check if user can create editorial content
  const [canWriteArticle, setCanWriteArticle] = useState(false);

  // Initial fetch on mount
  useEffect(() => {
    isMounted.current = true;
    getFeedCardsAction(dispatch, 1);

    // Check editorial permission
    checkEditorialPermissionAction().then((result) => {
      if (isMounted.current) {
        setCanWriteArticle(result.can_create);
      }
    });

    return () => {
      isMounted.current = false;
    };
  }, [dispatch]);

  // Load more for infinite scroll
  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    getFeedCardsAction(dispatch, currentPage + 1);
  }, [loading, hasMore, currentPage, dispatch]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const showEmptyState = !loading && cards.length === 0;

  return (
    <div className="w-100 d-flex pt-4 flex-column">
      <div className="px-0">
        {/* ── Leaderboard Widget (pinned to top per PRD §5.2) ──── */}
        <LeaderboardWidget />

        {/* ── Write Article button (only for assigned creators) ── */}
        {canWriteArticle && (
          <Link to="/write-article" style={{ textDecoration: "none" }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "12px 20px",
              margin: "12px 0",
              background: "#fff",
              border: "1px solid #bfdbfe",
              borderRadius: "12px",
              color: "#3a6ed4",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#eff6ff";
              e.currentTarget.style.borderColor = "#3a6ed4";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.borderColor = "#bfdbfe";
            }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              Write an Article
            </div>
          </Link>
        )}

        {/* ── Loading state (first load) ──────────────────────── */}
        {loading && cards.length === 0 && <SectionLoader />}

        {/* ── Activity Cards Timeline ──────────────────────────── */}
        {cards.length > 0 &&
          cards.map((card) => (
            <FeedCardDispatcher key={card.id} card={card} />
          ))}

        {/* ── Infinite scroll sentinel ─────────────────────────── */}
        {hasMore && (
          <div ref={sentinelRef} style={{ padding: "20px", textAlign: "center" }}>
            {loading && <span style={{ color: "#94a3b8", fontSize: "13px" }}>Loading more...</span>}
          </div>
        )}

        {/* ── Empty State (PRD §10) ────────────────────────────── */}
        {showEmptyState && (
          <div className="workbrook-empty-state">
            <div className="workbrook-empty-icon">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#3a6ed4"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <div className="workbrook-empty-title">Nothing new this week yet</div>
            <div className="workbrook-empty-message">
              Check back soon for jobs, referrals, and career insights — or
              head to the Community tab to see what others are sharing.
            </div>
            <button className="workbrook-empty-cta" onClick={onSwitchToCommunity}>
              Go to Community
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkbrookTab;
